import { useState, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Sparkles, Menu, X } from "lucide-react";
import { useAuth } from "@/api/auth";
import { FofyChat } from "@/components/FofyChat";
import GetYourDesign from "@/components/GetYourDesign";
// 16 Oct founder law: header Login opens the IN-PAGE login popup — never a
// navigation (DR-0006 amendment; same modal as HomeOld/Create3D gates).
import LoginModal from "@/components/LoginModal";

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // designers.fofus.in is a work view — customer-browse tabs stay hidden there
  const isDesignersSite = window.location.hostname.startsWith("designers.");
  // account.fofus.in = FOFUS Account (customer account hub, 02 Oct)
  const isAccountSite = window.location.hostname.startsWith("account.");
  // 09-10: designai.fofus.in had TWO floating bubbles — index.html injects fofy.js
  // (universal widget, host-gates only designers.*) AND React <FofyChat/> mounted here
  // for logged-in users. Ownership rule: fofy.js is the ONLY bubble on designai.*
  // (it already does SSO sessions via the fofus_sso cookie). FofyChat stays for
  // fofus.in / customer app hosts.
  const isDesignAiSite = window.location.hostname.startsWith("designai.");
  // 16 Oct founder law: "Get your design" opens the in-page need-capture dialog,
  // never a navigation. This session RE-INSTALLED the dialog (button wiring + state).
  const [gydOpen, setGydOpen] = useState(false);
  // 16 Oct founder law: header Login = the in-page popup (no navigation)
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // 29 Oct founder law: NO Fofy hero on designai OR account pages — the
  // greet strip ('Hi, I'm Fofy.' + tiles) is fofus.in-home chrome only.
  useEffect(() => {
    if (isDesignAiSite || isAccountSite) return;
    if (document.getElementById("fofus-greet-script")) return;
    const s = document.createElement("script");
    s.id = "fofus-greet-script";
    s.src = "https://fofus.in/js/fofus-greet.js";
    s.defer = true;
    document.head.appendChild(s);
  }, []);

  // Inject fofus-chat.js if not already loaded (non-authenticated users get external chat).
  // designers.fofus.in: NO AI bubble at all (user law 09-04 — AI talks in cards).
  useEffect(() => {
    if (user) return; // Authenticated users get the React FofyChat component instead
    if (isDesignersSite || isDesignAiSite) return; // designer work view + designai (29 Oct founder: no Fofy chrome there)
    if (document.getElementById("fofus-chat-script")) return;
    const s = document.createElement("script");
    s.id = "fofus-chat-script";
    s.src = "https://fofus.in/js/fofus-chat.js";
    s.defer = true;
    document.head.appendChild(s);
  }, [user]);

  // Amendment K (09-21): designer surfaces live only on designers.fofus.in.
  // On designai a designer-role user gets the designai customer dashboard.
  const dashboardPath =
    user?.role === "designer"
      ? isDesignersSite
        ? "/designer/board"
        : "/customer/dashboard"
      : user?.role === "admin"
        ? "/admin"
        : "/customer/dashboard";

  const handleLogout = () => {
    logout();
    setMobileOpen(false);
    navigate("/");
  };

  return (
    <div className="flex min-h-screen flex-col bg-ink-200">
      {/* ── Minimal top bar — just logo + account ─────────── */}
      <header className="sticky top-0 z-50 border-b border-ink-100 bg-ink-200/90 backdrop-blur-xl">
        <nav className="container-px flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-hero shadow-glow">
              <Sparkles className="h-5 w-5 text-ink-900" strokeWidth={2.5} />
            </span>
            <span className="text-xl font-bold tracking-tight text-ink-900">
              FOFUS
            </span>
          </Link>

          {/* Right actions — minimal */}
          <div className="flex items-center gap-3">
            {/* (account anchor removed 17 Oct — founder merge law: ONE account
                surface, the in-app Profile/Dashboard; the external account.fofus.in
                link was the same thing twice + a full-page exit) */}
            {/* COMMUNITY nav links — ONLY the community surfaces (designers/store
                context). demoted to secondary row on md; NOT primary products.
                (16 Oct founder note: designai products were unreachable from nav) */}
            {!isDesignersSite && (
              <div className="hidden md:flex items-center gap-1 opacity-80">
                <Link to="/explore" className="btn-ghost">Explore</Link>
              </div>
            )}

            {/* PRODUCT nav (D1 ladder law 16 Oct, amended after the Explore-merge
                (founder 16 Oct): FindModels NAV ITEM REMOVED — Explore IS the
                library surface now; Make3D is the creation front door. */}
            {!isDesignersSite && !isAccountSite && (
              <>
                <Link to="/create-3d" className="btn-ghost hidden md:inline-flex">
                  Make3D
                </Link>
                {/* 28 Oct: the 17 parametric makers were ORPHANED (no nav, no
                    inbound links) — customers could not reach Quick Design
                    without the footer. Founder: all four doors reachable. */}
                <Link to="/makers" className="btn-ghost hidden md:inline-flex">
                  Quick Design
                </Link>
                <Link to="/designers" className="btn-ghost hidden md:inline-flex">
                  Hire a Designer
                </Link>
              </>
            )}
            {/* Designer nav links — designer surfaces live on designers.fofus.in
                only (09-17 portal law); customer hosts keep customer chrome.
                Amendment K (09-21): on designai even a designer-role user's
                Dashboard button goes to /customer/dashboard (designai features). */}
            {isDesignersSite && user?.role === "designer" && (
              <>
                <Link to="/designer/board" className="btn-ghost hidden lg:inline-flex">
                  Board
                </Link>
                <Link to="/designer/models/new" className="btn-ghost hidden lg:inline-flex">
                  Upload Model
                </Link>
                <Link to="/designer/opportunities" className="btn-ghost hidden lg:inline-flex">
                  Opportunities
                </Link>
                <Link to="/designer/earnings" className="btn-ghost hidden lg:inline-flex">
                  Earnings
                </Link>
              </>
            )}
            {user ? (
              <>
                <Link to={dashboardPath} className="btn-ghost hidden sm:inline-flex">
                  Dashboard
                </Link>
                {!isDesignersSite && (
                  <Link to="/customer/profile" className="btn-ghost hidden sm:inline-flex">
                    Profile
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="btn-primary hidden sm:inline-flex"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setLoginOpen(true)} className="btn-ghost hidden sm:inline-flex">
                  Login
                </button>
                {isDesignersSite ? (
                  <Link to="/register?role=designer" className="btn-primary hidden sm:inline-flex">
                    Apply as a Designer
                  </Link>
                ) : (
                  <button onClick={() => setGydOpen(true)} className="btn-primary hidden sm:inline-flex">
                    Get Your Design
                  </button>
                )}
              </>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-600 transition-colors hover:bg-ink-100 md:hidden"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </nav>

        {/* Mobile menu — simple links only */}
        {mobileOpen && (
          <div className="border-t border-ink-100 bg-ink-200 px-4 py-3 md:hidden">
            <div className="flex flex-col gap-2">
              {user ? (
                <>
                  <Link
                    to={dashboardPath}
                    onClick={() => setMobileOpen(false)}
                    className="btn-secondary w-full"
                  >
                    Dashboard
                  </Link>
                  {/* (My Account external link removed 17 Oct — merge law, see desktop) */}
                  {!isDesignersSite && (
                    <Link
                      to="/customer/profile"
                      onClick={() => setMobileOpen(false)}
                      className="btn-secondary w-full"
                    >
                      Profile
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="btn-primary w-full"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { setMobileOpen(false); setLoginOpen(true); }}
                    className="btn-secondary w-full"
                  >
                    Login
                  </button>
                  {!isDesignersSite && (
                    <button
                      onClick={() => { setMobileOpen(false); setGydOpen(true); }}
                      className="btn-primary w-full"
                    >
                      Get Your Design
                    </button>
                  )}
                </>
              )}
              {/* Quick links to community pages — hidden on designers (work view) */}
              {!isDesignersSite && (
                <div className="mt-2 border-t border-ink-100 pt-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-500">
                    Community
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Link to="/create-3d" onClick={() => setMobileOpen(false)} className="rounded-lg px-3 py-2 text-sm text-ink-700 hover:bg-ink-100">Make3D</Link>
                    <Link to="/explore" onClick={() => setMobileOpen(false)} className="rounded-lg px-3 py-2 text-sm text-ink-700 hover:bg-ink-100">Explore</Link>
                  </div>
                </div>
              )}
              {/* Quick links to other FOFUS sites */}
              <div className="mt-2 border-t border-ink-100 pt-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-500">
                  FOFUS Sites
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <a href="https://store.fofus.in" className="rounded-lg px-3 py-2 text-sm text-ink-700 hover:bg-ink-100">🛍️ Shop</a>
                  <a href="https://quote.fofus.in" className="rounded-lg px-3 py-2 text-sm text-ink-700 hover:bg-ink-100">💰 Quote</a>
                  <a href="https://fofus.in/scan" className="rounded-lg px-3 py-2 text-sm text-ink-700 hover:bg-ink-100">📷 Scan</a>
                  <a href="https://platform.fofus.in" className="rounded-lg px-3 py-2 text-sm text-ink-700 hover:bg-ink-100">🚀 Launch</a>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main content — fofus-greet.js injects conversational front here */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-ink-100 bg-ink-50">
        <div className="container-px py-12">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Link to="/" className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-hero">
                  <Sparkles className="h-4 w-4 text-ink-900" strokeWidth={2.5} />
                </span>
                <span className="text-lg font-bold text-ink-900">FOFUS</span>
              </Link>
              <p className="mt-3 text-sm text-ink-500">
                AI-powered design marketplace. From idea to design, powered by intelligence.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-ink-900">Marketplace</h4>
              <ul className="mt-3 space-y-2 text-sm text-ink-500">
                {!isDesignersSite && (
                  <>
                    <li><Link to="/explore" className="hover:text-primary-600 transition-colors">Explore Designs</Link></li>
                    <li><Link to="/designers" className="hover:text-primary-600 transition-colors">Browse Designers</Link></li>
                    <li><Link to="/collections" className="hover:text-primary-600 transition-colors">Collections</Link></li>
                  </>
                )}
                {!isDesignersSite && <li><Link to="/scan/start" className="hover:text-primary-600 transition-colors">Start a Project</Link></li>}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-ink-900">Company</h4>
              <ul className="mt-3 space-y-2 text-sm text-ink-500">
                <li><a href="https://fofus.in/about" className="hover:text-primary-600 transition-colors">About</a></li>
                <li><a href="https://fofus.in" className="hover:text-primary-600 transition-colors">Home</a></li>
                <li><a href="https://store.fofus.in" className="hover:text-primary-600 transition-colors">Shop</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-ink-900">Support</h4>
              <ul className="mt-3 space-y-2 text-sm text-ink-500">
                <li><a href="https://wa.me/919895854640" className="hover:text-primary-600 transition-colors">WhatsApp Us</a></li>
                <li><a href="https://quote.fofus.in" className="hover:text-primary-600 transition-colors">Get a Quote</a></li>
                <li><a href="https://fofus.in/scan" className="hover:text-primary-600 transition-colors">3D Scanning</a></li>
              </ul>
            </div>
          </div>

          <div className="mt-10 flex flex-col items-center justify-between border-t border-ink-300 pt-6 sm:flex-row">
            <p className="text-sm text-ink-600">
              © {new Date().getFullYear()} FOFUS. All rights reserved.
            </p>
            <p className="mt-2 text-sm text-ink-600 sm:mt-0">
              Made with <span className="text-primary-600">♥</span> in India
            </p>
          </div>
        </div>
      </footer>

      {/* Fofy Chat — authenticated users only. designers.fofus.in serves
          DESIGNERS (design providers, not customers): their AI surface is the
          FOFUS Studio dock (StudioDock, App.tsx host-gate) — the customer
          chat bubble stays off the designer site. */}
      {user && !isDesignersSite && !isDesignAiSite && <FofyChat />}

      {/* 16 Oct: Get-your-design dialog (in-page need capture; never navigates).
          Brand: the CTA carries the Make3D subbrand but OPENS the dialog. */}
      <GetYourDesign open={gydOpen} onClose={() => setGydOpen(false)} />

      {/* 16 Oct founder law: in-page login popup on EVERY customer surface —
          the header Login button never navigates. */}
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}
